import { Avatar } from '@/components/ui';
import { formatBRLShort, formatDate, formatNumber } from '@/lib/format';
import type { Metrics } from '@/lib/types';
import { METRIC_TOKENS } from './MetricsLedger';

/** Valor com uma barra fina embaixo, proporcional ao maior da coluna. */
function ShareCell({ value, max, token }: { value: number; max: number; token: string }) {
  const width = max > 0 ? (value / max) * 100 : 0;
  return (
    <span className="share-cell">
      <span>{formatBRLShort(value)}</span>
      <span className="share-bar" aria-hidden>
        {width > 0 ? <span style={{ width: `${Math.max(width, 2)}%`, background: `var(${token})` }} /> : null}
      </span>
    </span>
  );
}

/** Vendas do período e depósitos de hoje, membro a membro. */
export function MembersTable({ members }: { members: Metrics['members'] }) {
  const maxSold = Math.max(0, ...members.map((member) => member.soldTotal));
  const maxDeposit = Math.max(0, ...members.map((member) => member.deposit.total));

  return (
    <div className="table-wrap">
      <table className="tbl tbl-cards metrics-members">
        <thead>
          <tr>
            <th scope="col">Membro</th>
            <th scope="col" className="right">Vendido</th>
            <th scope="col" className="right">Comprov.</th>
            <th scope="col" className="right">Na corretora</th>
            <th scope="col" className="right">Clientes c/ depósito</th>
          </tr>
        </thead>
        <tbody>
          {members.map((member) => (
            <tr key={member.id} className="row-hover">
              <td className="cell-main">
                <span className="member-cell">
                  <Avatar name={member.name} color={member.avatarColor} photo={member.photoUrl} size={26} />
                  <span style={{ fontWeight: 500 }}>{member.name}</span>
                  {!member.active ? <span className="chip">inativo</span> : null}
                </span>
              </td>
              <td className="num right cell-key" data-label="Vendido">
                <ShareCell value={member.soldTotal} max={maxSold} token={METRIC_TOKENS.sold} />
              </td>
              <td className="num right muted" data-label="Comprovantes">
                {formatNumber(member.sales.receiptCount + member.leverage.receiptCount)}
              </td>
              <td className="num right" data-label="Na corretora">
                <ShareCell value={member.deposit.total} max={maxDeposit} token={METRIC_TOKENS.deposit} />
              </td>
              <td className="num right muted" data-label="Clientes c/ depósito">{formatNumber(member.deposit.leadCount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface RankedClient {
  id: string;
  name: string;
  ownerName: string | null;
  value: number;
  detail: string;
}

/** Uma lista curta e numerada de clientes — a posição é a ordem do valor. */
function ClientRanking({ clients, showOwner, empty }: { clients: RankedClient[]; showOwner: boolean; empty: string }) {
  if (clients.length === 0) return <p className="ranking-empty">{empty}</p>;
  return (
    <ol className="client-ranking">
      {clients.map((client, index) => (
        <li key={client.id}>
          <span className="client-ranking-pos num">{index + 1}</span>
          <span className="client-ranking-who">
            <span className="truncate client-ranking-name">{client.name}</span>
            <span className="client-ranking-detail">
              {showOwner ? `${client.ownerName ?? 'Sem responsável'} · ` : ''}
              {client.detail}
            </span>
          </span>
          <span className="num client-ranking-value">{formatBRLShort(client.value)}</span>
        </li>
      ))}
    </ol>
  );
}

export function TopClients({ clients, showOwner }: { clients: Metrics['topClients']; showOwner: boolean }) {
  return (
    <ClientRanking
      showOwner={showOwner}
      empty="Nenhum comprovante ligado a um cliente no período."
      clients={clients.map((client) => ({
        id: client.id,
        name: client.name,
        ownerName: client.ownerName,
        value: client.soldTotal,
        detail: `${formatNumber(client.receiptCount)} ${client.receiptCount === 1 ? 'comprovante' : 'comprovantes'}`,
      }))}
    />
  );
}

export function TopDepositors({ clients, showOwner }: { clients: Metrics['topDepositors']; showOwner: boolean }) {
  return (
    <ClientRanking
      showOwner={showOwner}
      empty="Nenhum cliente com depósito informado."
      clients={clients.map((client) => ({
        id: client.id,
        name: client.name,
        ownerName: client.ownerName,
        value: client.deposit,
        detail: client.depositUpdatedAt ? `atualizado em ${formatDate(client.depositUpdatedAt)}` : 'sem data',
      }))}
    />
  );
}
